<template>
  <div
    ref="modalEl"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="imeiHistoryTitle"
    aria-hidden="true"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="imeiHistoryTitle">
            Andere IMEIs {{ device?.stock_number ?? '' }}
          </h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Schließen"
          ></button>
        </div>

        <div v-if="device" class="modal-body">
          <p class="imei-current">
            Aktuell: <span class="imei-mono">{{ device.imei_sn || '—' }}</span>
          </p>

          <p v-if="device.previous_imeis.length === 0" class="imei-empty">
            Die IMEI dieses Geräts wurde nie gewechselt.
          </p>

          <!-- Neueste zuerst — so liest man die Kette von jetzt nach hinten. -->
          <ol v-else class="imei-list">
            <li v-for="(wechsel, i) in device.previous_imeis" :key="i" class="imei-entry">
              <div class="imei-chain">
                <span class="imei-mono">{{ wechsel.previous_imei_sn ?? '(keine)' }}</span>
                <span class="imei-arrow" aria-hidden="true">→</span>
                <span class="imei-mono">{{ wechsel.new_imei_sn ?? '(keine)' }}</span>
              </div>
              <div class="imei-meta">
                {{ reasonLabel(wechsel.reason) }} · {{ formatDate(wechsel.change_timestamp) }}
              </div>
            </li>
          </ol>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-primary" data-bs-dismiss="modal">Schließen</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import { Modal } from 'bootstrap';
import type { StockDevice } from '../services/stockDevices';

/**
 * Die früheren IMEIs eines Lagergeräts.
 *
 * Ein eigener Dialog und keine Zeile im Detail-Dialog: der Normalfall ist, dass
 * die IMEI nie gewechselt wurde — dann hat die Kette dort nichts zu suchen. Wurde
 * sie gewechselt (SWAP beim Servicepartner), darf die Nummer, unter der das Gerät
 * hereinkam, nicht verloren gehen.
 *
 * Die Daten kommen aus dem übergebenen Gerät (`previous_imeis`), es wird nichts
 * nachgeladen.
 */
const modalEl = useTemplateRef<HTMLDivElement>('modalEl');
let   modal: Modal | null = null;
let   resolver: (() => void) | null = null;

const device = ref<StockDevice | null>(null);

onMounted(() => {
  if (modalEl.value) {
    modal = new Modal(modalEl.value);
    modalEl.value.addEventListener('hidden.bs.modal', onHidden);
  }
});

onBeforeUnmount(() => {
  modalEl.value?.removeEventListener('hidden.bs.modal', onHidden);
  modal?.dispose();
  modal = null;
});

function open(item: StockDevice): Promise<void> {
  device.value = item;
  return new Promise<void>(resolve => {
    resolver = resolve;
    modal?.show();
  });
}

function onHidden(): void {
  resolver?.();
  resolver = null;
  device.value = null;
}

/** „swap" → „SWAP beim Servicepartner". Heute der einzige Anlass. */
function reasonLabel(reason: string): string {
  return reason === 'swap' ? 'SWAP beim Servicepartner' : reason.toUpperCase();
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('de-AT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(d);
}

defineExpose({ open });
</script>

<style scoped>
.imei-current {
  margin-bottom: 16px;
  color: #6c757d;
}

.imei-mono {
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
  color: #212529;
}

.imei-empty {
  margin: 0;
  color: #6c757d;
}

.imei-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.imei-entry {
  padding: 10px 0;
}

.imei-entry + .imei-entry {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.imei-chain {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.imei-arrow { color: #6c757d; }

.imei-meta {
  margin-top: 2px;
  font-size: 13px;
  color: #6c757d;
}
</style>
